import classNames from "classnames";
import { formatDistanceToNow } from "date-fns";
import { useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { TooltipWrapper } from "../../../components/tooltip/TooltipWrapper";
import { ROUTES } from "../../../routes";
import { useReportJobs } from "../rater/useIntegrationJobs";
import styles from "./IntegrationJobsCell.module.css";
import { ThemeContext } from "../../../themes/Theme";

export type DisplayedType = "RMS" | "ICM" | "RATER" | "POLICY_FLY";

const INTEGRATION_TYPE_INFO: Record<
  DisplayedType,
  { label: string; letter: string; tab: string }
> = {
  RMS: { label: "RMS", letter: "M", tab: "cat" },
  ICM: { label: "ICM", letter: "I", tab: "cat" },
  POLICY_FLY: { label: "Policy Fly", letter: "P", tab: "general" },
  RATER: { label: "Rater", letter: "R", tab: "rater" },
};

const DISPLAYED_TYPES: DisplayedType[] = ["RMS", "ICM", "RATER", "POLICY_FLY"];

const infoForType = (type: string) =>
  INTEGRATION_TYPE_INFO[type as DisplayedType] ?? {
    label: type,
    letter: type[0]?.toUpperCase() ?? "?",
    tab: "general",
  };

export type JobResult =
  | { status: "none" }
  | { status: "in_progress" }
  | { status: "success"; timeAgo: string }
  | { status: "failed"; timeAgo: string };

export function jobResult(
  job:
    | { finished_at: string | null; outcome: "success" | "failure" | null }
    | undefined
): JobResult {
  if (!job) return { status: "none" };
  if (!job.finished_at) return { status: "in_progress" };
  const timeAgo = formatDistanceToNow(new Date(job.finished_at + "Z"), {
    addSuffix: true,
  });
  return job.outcome === "success"
    ? { status: "success", timeAgo }
    : { status: "failed", timeAgo };
}

/** Build a map from integration_type → latest job (by started_at). */
export function latestJobByType<
  T extends { integration_type: string; started_at: string },
>(jobs: T[]): Record<string, T> {
  const map: Record<string, T> = {};
  for (const job of jobs) {
    const current = map[job.integration_type];
    if (!current || job.started_at > current.started_at) {
      map[job.integration_type] = job;
    }
  }
  return map;
}

function tooltipText(label: string, result: JobResult): string {
  switch (result.status) {
    case "none":
      return `${label} — Not run`;
    case "in_progress":
      return `${label} — In progress`;
    case "success":
      return `${label} — Finished ${result.timeAgo}`;
    case "failed":
      return `${label} — Failed ${result.timeAgo}`;
  }
}

export const IntegrationJobsCell = ({ reportId }: { reportId: string }) => {
  const { theme } = useContext(ThemeContext);
  const isLightMode = theme === "light";
  const navigate = useNavigate();
  const { jobs } = useReportJobs(reportId);

  const latestByType = useMemo(() => latestJobByType(jobs), [jobs]);

  const rerunJob = latestByType["RERUN_REPORT"];
  const isRerunning = rerunJob && !rerunJob.finished_at;

  if (isRerunning) {
    return (
      <TooltipWrapper tooltipText="Rerunning report...">
        <div
          className={classNames(
            styles.chip,
            styles.chipInProgress,
            styles.rerunChip
          )}
        >
          <div className={styles.rerunRipple} />
          Re-running
        </div>
      </TooltipWrapper>
    );
  }

  return (
    <div className={styles.container}>
      {DISPLAYED_TYPES.map((type, i) => {
        const { label, letter, tab } = infoForType(type);
        const result = jobResult(latestByType[type]);
        const chipClass = classNames(
          styles.chip,
          {
            [styles.chipSuccess]: result.status === "success",
            [styles.chipFailed]: result.status === "failed",
            [styles.chipInProgress]: result.status === "in_progress",
            [styles.chipNone]: result.status === "none",
          },
          isLightMode ? styles.lightMode : styles.darkMode
        );
        return (
          <div
            key={type}
            style={{
              marginLeft: i > 0 ? -8 : 0,
              zIndex: DISPLAYED_TYPES.length - i,
            }}
          >
            <TooltipWrapper tooltipText={tooltipText(label, result)}>
              <div
                className={styles.chipWrapper}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(ROUTES.DASHBOARD_REPORT(reportId, tab));
                }}
              >
                <div className={chipClass}>
                  {result.status === "in_progress" && (
                    <div className={styles.ripple} />
                  )}
                  {letter}
                </div>
              </div>
            </TooltipWrapper>
          </div>
        );
      })}
    </div>
  );
};
